import * as React from "react";
import { addDays, differenceInCalendarDays, format } from "date-fns";
import { de, enUS, nb, type Locale as DateFnsLocale } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { cn } from "lib/utils";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Calendar } from "./calendar";

export interface DateRangeValue {
  from: Date | null;
  to: Date | null;
}

export interface DateRangePickerProps {
  value?: DateRangeValue | null;
  onChange?: (range: DateRangeValue | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
  "aria-label"?: string;
  isDateDisabled?: (date: Date) => boolean;
  /**
   * When a full range is already selected and the user clicks a new start day,
   * automatically shift the end day by the same duration to preserve range length.
   */
  preserveDurationOnStartChange?: boolean;
  // Show a clear control inside the popover footer
  clearable?: boolean;
  // Show a compact clear icon next to the trigger (outside the button)
  showClearInTrigger?: boolean;
  /**
   * Month to display when no range is selected yet. Lets the consumer open the
   * calendar on the first bookable month instead of the current month when a
   * lead time pushes the earliest selectable date into a later month.
   */
  defaultMonth?: Date;
}

export function DateRangePicker({
  value: valueProp,
  onChange,
  placeholder,
  disabled,
  className,
  buttonClassName,
  isDateDisabled,
  preserveDurationOnStartChange = false,
  clearable = false,
  showClearInTrigger = false,
  defaultMonth,
  ...rest
}: DateRangePickerProps) {
  const { i18n, t } = useTranslation();
  const locale = React.useMemo<DateFnsLocale>(() => {
    const lang = (i18n?.language || "en").toLowerCase();
    if (lang.startsWith("de")) return de;
    if (lang.startsWith("nb")) return nb;
    return enUS;
  }, [i18n?.language]);

  const computedPlaceholder =
    placeholder ??
    t("pages.placeholders.zeitraum_auswaehlen", { defaultValue: "Pick a date range" });

  const [open, setOpen] = React.useState(false);
  const isControlled = valueProp !== undefined;
  const [internalRange, setInternalRange] = React.useState<DateRangeValue | null>(
    valueProp ?? { from: null, to: null }
  );
  const range = isControlled ? valueProp ?? { from: null, to: null } : internalRange;


  const emitChange = React.useCallback(
    (next: DateRangeValue | null) => {
      if (!isControlled) setInternalRange(next);
      onChange?.(next);
    },
    [isControlled, onChange]
  );

  const onSelectRange = (
    next?: { from?: Date; to?: Date },
    selectedDate?: Date
  ) => {
    // Click-position semantics for editing a complete range
    // (booking.com-style):
    //   - click before `from` → that becomes the new `from` (keep `to`)
    //   - click after  `to`   → that becomes the new `to`   (keep `from`)
    //   - click inside range  → start a fresh range anchored on the click
    //
    // Plus: if the existing range is a single-day {X, X} and react-day-picker
    // emits empty/null (its native deselect behavior on a second click of an
    // already-selected single day), commit it as a single-day rather than
    // deselecting — the user confirms with the Done button below.
    const isCompleteRange = !!range?.from && !!range?.to;

    let normalized: DateRangeValue | null;
    if (isCompleteRange) {
      const fromTime = range!.from!.getTime();
      const toTime = range!.to!.getTime();
      const isSingleDay = fromTime === toTime;

      if (isSingleDay && (!next || (!next.from && !next.to))) {
        // Same-day deselect → commit as single-day (preserve range).
        normalized = { from: range!.from, to: range!.to };
      } else if (selectedDate) {
        const clickTime = selectedDate.getTime();
        if (clickTime < fromTime) {
          normalized = { from: selectedDate, to: range!.to };
        } else if (clickTime > toTime) {
          normalized = { from: range!.from, to: selectedDate };
        } else {
          // Click inside the range → fresh range anchored on the click.
          normalized = { from: selectedDate, to: null };
        }
      } else if (next) {
        normalized = { from: next.from ?? null, to: next.to ?? null };
      } else {
        normalized = { from: null, to: null };
      }
    } else if (next) {
      // Incomplete range — fall through to react-day-picker's natural
      // behavior for picking the second endpoint.
      normalized = { from: next.from ?? null, to: next.to ?? null };
    } else {
      normalized = { from: null, to: null };
    }

    // Optionally preserve duration when only a new start is chosen after a full range
    if (
      preserveDurationOnStartChange &&
      range?.from &&
      range?.to &&
      next?.from &&
      !next?.to
    ) {
      const len = Math.max(0, differenceInCalendarDays(range.to, range.from));
      const newTo = addDays(next.from, len);
      // If the computed end is disabled, fallback to letting user pick end manually
      if (!isDateDisabled || (!isDateDisabled(next.from) && !isDateDisabled(newTo))) {
        normalized = { from: next.from, to: newTo };
      } else {
        normalized = { from: next.from, to: null };
      }
    }

    emitChange(normalized);
  };

  const buttonLabel = React.useMemo(() => {
    if (!range?.from && !range?.to) return computedPlaceholder;
    const fromStr = range?.from ? format(range.from, "P", { locale }) : "";
    const toStr = range?.to ? format(range.to, "P", { locale }) : "";
    return [fromStr, toStr].filter(Boolean).join(" – ");
  }, [range?.from, range?.to, computedPlaceholder, locale]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative inline-block">
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-[260px] justify-start text-left font-normal",
              !(range?.from && range?.to) && "text-muted-foreground",
              buttonClassName
            )}
            disabled={disabled}
            aria-label={rest["aria-label"] ?? computedPlaceholder}
          >
            <CalendarIcon className="mr-2 h-4 w-4" aria-hidden="true" />
            {buttonLabel}
          </Button>
        </PopoverTrigger>
        {showClearInTrigger && (range?.from || range?.to) && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-3 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full z-10"
            aria-label={rest["aria-label"] ?? computedPlaceholder}
            onClick={() => {
              const cleared = { from: null, to: null } as DateRangeValue;
              if (!isControlled) setInternalRange(cleared);
              onChange?.(cleared);
              setOpen(false);
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      <PopoverContent className={cn("w-auto p-0", className)} align="start">
        <div className="p-3">
          <Calendar
            mode="range"
            selected={{ from: range?.from ?? undefined, to: range?.to ?? undefined }}
            onSelect={onSelectRange as any}
            defaultMonth={range?.from ?? defaultMonth ?? undefined}
            locale={locale}
            disabled={isDateDisabled}
            initialFocus
          />
          <div className="mt-3 flex items-center justify-end gap-2">
            {clearable && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const cleared = { from: null, to: null } as DateRangeValue;
                  if (!isControlled) setInternalRange(cleared);
                  onChange?.(cleared);
                  setOpen(false);
                }}
              >
                {t("common.clear", { defaultValue: "Clear" })}
              </Button>
            )}
            <Button
              variant="default"
              size="sm"
              onClick={() => setOpen(false)}
            >
              {t("common.done", { defaultValue: "Done" })}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default DateRangePicker;
